import {
  Flex,
  Text,
  useColorModeValue,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Switch,
  Stack,
  Box,
  Select,
  Textarea,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { columnsDataChannel } from 'variables/columnsData';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { totalRow } from 'state/roles-permissions';
import { AddIcon } from '@chakra-ui/icons';
import { checkForbidden } from 'helpers/checkForbidden';
import { BsSearch } from 'react-icons/bs';
import { RxReset } from 'react-icons/rx';
import { useBookingActions } from 'hooks/useBookingActions';
import { listChannelAtom } from 'state/show';
import SearchTable2 from 'components/Tables/SearchTable2';
import { STATUS } from 'variables/general';
import { useChannelGroupActions } from 'hooks/useChannelGroupActions';

function DataTableChannels() {
  const textColor = useColorModeValue('gray.700', 'white');
  const listChannel = useRecoilValue(listChannelAtom);
  const totalRows = useRecoilValue(totalRow);
  const bookingActions = useBookingActions();
  const channelGroupActions = useChannelGroupActions();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCreate, setIsCreate] = useState(true);
  const [currentChannelID, setCurrentChannelID] = useState();
  const [isFetching, setIsFetching] = useState(false);
  const [newListChannels, setNewListChannels] = useState([]);
  const [refreshPagingDefault, setRefreshPagingDefault] = useState(false);
  const [channelGroups, setChannelGroups] = useState([]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('This field is required'),
  });
  const formOptions = { resolver: yupResolver(validationSchema) };
  const { register, handleSubmit, formState, reset, setValue } = useForm(
    formOptions
  );
  const {
    register: registerSearch,
    handleSubmit: handleSubmitSearch,
    watch: watchSearch,
    reset: resetSearch,
  } = useForm();
  const { errors, isSubmitting } = formState;
  const toast = useToast();
  const nameSearch = watchSearch('nameSearch');
  const statusSearch = watchSearch('statusSearch');
  const channelGroupIdSearch = watchSearch('channelGroupIdSearch');

  useEffect(() => {
    loadChannelGroups();
  }, []);

  async function loadChannelGroups() {
    try {
      const res = await channelGroupActions.getChannelGroupsAll();
      if (res && res.data && res.data.data) {
        setChannelGroups(res.data.data);
      }
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (listChannel) {
      const newListChannels = listChannel.map((i) => ({
        ...i,
        channel_group_name: channelGroups.find((g) => g.id === i.channel_group_id)?.group_name || '',
        typeSwitch: 'channel',
        filters: { name: nameSearch, status: statusSearch ? statusSearch : 1, channel_group_id: channelGroupIdSearch },
      }));
      setNewListChannels(newListChannels);
    }
  }, [listChannel, channelGroups]);

  async function onSubmitSearch({ nameSearch, statusSearch, channelGroupIdSearch }) {
    setIsFetching(true);
    await bookingActions.getListChannel(
      { name: nameSearch, status: statusSearch, channel_group_id: channelGroupIdSearch },
      0,
      localStorage && localStorage.getItem('currentPageSizeStore')
        ? localStorage.getItem('currentPageSizeStore')
        : 10
    );
    setRefreshPagingDefault(true);
    setIsFetching(false);
  }

  const recallApi = async (fetchDataOptions) => {
    setIsFetching(true);
    await bookingActions.getListChannel(
      {
        name: nameSearch,
        status: statusSearch ? statusSearch : 1,
        channel_group_id: channelGroupIdSearch,
        sort_type: fetchDataOptions.sort_type,
        sort_field: fetchDataOptions.sort_field,
      },
      fetchDataOptions.pageIndex,
      fetchDataOptions.pageSize
    );
    setIsFetching(false);
  };

  async function onSubmit({ name, description, channel_group_id, isActive }) {
    const params = {
      name,
      description,
      channel_group_id: channel_group_id ? Number(channel_group_id) : null,
      status: isActive ? 1 : 2,
    };
    try {
      let res;
      if (isCreate) {
        res = await bookingActions.createChannel(params);
      } else {
        res = await bookingActions.updateChannel(params, currentChannelID);
      }

      if (res.status === 200) {
        onClose();
        reset();
        const message = isCreate
          ? 'Create Channel success'
          : 'Update Channel success';

        toast({
          description: message,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        await bookingActions.getListChannel(
          {
            name: nameSearch,
            status: statusSearch ? statusSearch : 1,
            channel_group_id: channelGroupIdSearch,
          },
          0,
          localStorage && localStorage.getItem('currentPageSizeStore')
            ? localStorage.getItem('currentPageSizeStore')
            : 10
        );
        setRefreshPagingDefault((prev) => !prev);
      }
    } catch (err) {
      console.warn(err);
      toast({
        description: err?.response?.data?.meta?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  async function editChannel(channelID) {
    const channelDetail = await bookingActions.getChannelDetail(channelID);
    if (channelDetail) {
      const { name, description, id, status, channel_group_id } = channelDetail.data.data;
      setValue('name', name);
      setValue('description', description);
      setValue('channel_group_id', channel_group_id ? String(channel_group_id) : '');
      setValue('isActive', status === 1 ? true : false);
      setCurrentChannelID(id);
    }
    setIsCreate(false);
    onOpen();
  }

  return (
    <Card px='0px'>
      <CardHeader px='22px' mb='12px'>
        <Flex direction='column' display='block'>
          <Text
            float='left'
            color={textColor}
            fontSize='lg'
            fontWeight='bold'
            mb='6px'
          >
            Channel Management
          </Text>
          <Stack float='right' direction='row' spacing={4}>
            {checkForbidden(14) && (
              <Button
                size='sm'
                onClick={() => {
                  onOpen();
                  setIsCreate(true);
                }}
                leftIcon={<AddIcon />}
                colorScheme='blue'
                variant='outline'
              >
                Add Channel
              </Button>
            )}
          </Stack>
          <Modal
            closeOnOverlayClick={false}
            isOpen={isOpen}
            onClose={() => {
              onClose();
              reset();
            }}
            size='2xl'
          >
            <ModalOverlay />
            <form onSubmit={handleSubmit(onSubmit)}>
              <ModalContent>
                <ModalHeader>
                  {isCreate ? 'Add New Channel' : 'Edit Channel'}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                  <FormControl isRequired>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Name
                    </FormLabel>
                    <Input
                      fontSize='sm'
                      ms='4px'
                      type='text'
                      placeholder='Name'
                      maxLength={250}
                      mb={
                        errors.name?.message ? '5px' : { lg: '24px', sm: '0' }
                      }
                      size='md'
                      name='name'
                      {...register('name')}
                      isInvalid={errors.name}
                      errorBorderColor='red.300'
                    />
                    <Text color='red' fontSize={14} mb='10px' ml='5px'>
                      {errors.name?.message}
                    </Text>
                  </FormControl>
                  <FormControl mb={4}>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Channel Group
                    </FormLabel>
                    <Select
                      ms='4px'
                      size='md'
                      {...register('channel_group_id')}
                    >
                      <option value=''>-- Select Channel Group --</option>
                      {channelGroups.map((g) => (
                        <option key={g.id} value={String(g.id)}>
                          {g.group_name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Description
                    </FormLabel>
                    <Textarea
                      rows={4}
                      {...register('description')}
                      maxLength={1000}
                      name='description'
                      mb={
                        errors.description?.message
                          ? '5px'
                          : { lg: '24px', sm: '0' }
                      }
                    />
                    <Text color='red' ml='5px'>
                      {errors.apiError && errors.apiError?.message}
                    </Text>
                  </FormControl>
                  <FormControl>
                    <Switch
                      id='isChecked'
                      defaultChecked={isCreate && true}
                      name='isActive'
                      {...register('isActive')}
                      size='sm'
                      ms='4px'
                    >
                      Active
                    </Switch>
                  </FormControl>
                </ModalBody>
                <ModalFooter>
                  <Button
                    colorScheme='blue'
                    mr={3}
                    type='submit'
                    isLoading={isSubmitting}
                  >
                    {isCreate ? 'Create' : 'Update'}
                  </Button>
                  <Button
                    onClick={() => {
                      onClose();
                      reset();
                    }}
                  >
                    Cancel
                  </Button>
                </ModalFooter>
              </ModalContent>
            </form>
          </Modal>
        </Flex>
      </CardHeader>
      <CardBody>
        <Card>
          <form
            id='user-search-form'
            onSubmit={handleSubmitSearch(onSubmitSearch)}
          >
            <Flex
              direction='row'
              alignItems='center'
              mb={3}
              gap={4}
              flexWrap={'wrap'}
            >
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel ms='4px' fontSize='xs' fontWeight='semibold'>
                  Channel Name
                </FormLabel>
                <Input
                  _placeholder={{ color: 'gray.400' }}
                  variant='main'
                  type='text'
                  placeholder='Name'
                  minW='80px'
                  maxW='185px'
                  size='sm'
                  fontSize='sm'
                  name='nameSearch'
                  {...registerSearch('nameSearch')}
                  _focus={{ borderColor: 'blue.500' }}
                />
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Status
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  defaultValue='1'
                  size='sm'
                  name='statusSearch'
                  {...registerSearch('statusSearch')}
                  width='100px'
                >
                  {STATUS.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Channel Group
                </FormLabel>
                <Select
                  ms='4px'
                  size='sm'
                  width='180px'
                  {...registerSearch('channelGroupIdSearch')}
                >
                  <option value=''>All</option>
                  {channelGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.group_name}
                    </option>
                  ))}
                </Select>
              </Box>
            </Flex>
            <Flex justify='center' align='center' gap='8px' mt={3}>
              <Box direction='row' alignItems='center' display='block'>
                <Button
                  leftIcon={<RxReset />}
                  variant='primary'
                  w='88px'
                  h='33px'
                  onClick={() => {
                    resetSearch();
                  }}
                >
                  Reset
                </Button>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <Button
                  leftIcon={<BsSearch />}
                  variant='primary'
                  w='88px'
                  h='33px'
                  type='submit'
                >
                  Search
                </Button>
              </Box>
            </Flex>
          </form>
        </Card>
        <SearchTable2
          tableData={newListChannels}
          columnsData={columnsDataChannel}
          totalRow={totalRows.channels}
          edit={editChannel}
          listPermissions={[13]}
          actionsActive={['edit']}
          loadData={(fetchDataOptions) => recallApi(fetchDataOptions)}
          isFetching={isFetching}
          refreshPagingDefault={refreshPagingDefault}
        />
      </CardBody>
    </Card>
  );
}

export default DataTableChannels;
